/**
 * Hover steps for an UNTOUCHED row whose pool carries weight rules: the
 * normal story ("your share of the whole pool") would neither match the
 * number nor mention that pins reserved part of the budget, so this one
 * tells the truth — what the pins took, what the free rows split, and what
 * the boosted rows drew from the rest.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { PreviewRow } from '../../../preview-row.types';
import type { AllocatorBounds, CalcStep } from './score-calculation';
import { siblingGroupKey } from './optimal-weight-steps';

const pct = (perMille: number): string => `${(perMille / 10).toFixed(1)}%`;
const score = (v: number | undefined): string => (v ?? 0).toFixed(1);

interface PoolRules {
  pinnedPerMille: number;
  boostAppliedPerMille: number;
}

/** Σ of what static pins reserved / dynamic boosts drew in this row's pool. */
export function poolWeightRuleTotals(
  row: PreviewRow,
  siblings: PreviewRow[],
): PoolRules {
  const key = siblingGroupKey(row);
  let pinnedPerMille = 0;
  let boostAppliedPerMille = 0;
  for (const s of siblings) {
    if (siblingGroupKey(s) !== key || s.passThrough || !s.weightRule) continue;
    if (s.weightRule.mode === 'static') pinnedPerMille += s.optimalPerMille;
    else boostAppliedPerMille += s.weightRule.appliedPerMille ?? 0;
  }
  return { pinnedPerMille, boostAppliedPerMille };
}

/** The free-row story when pins/boosts shaped this pool. */
export function buildFreeRowWithRulesSteps(
  row: PreviewRow,
  siblings: PreviewRow[],
  bounds: AllocatorBounds,
  rules: PoolRules,
): CalcStep[] {
  const key = siblingGroupKey(row);
  const free = siblings.filter(
    (s) =>
      siblingGroupKey(s) === key &&
      s.active &&
      !s.passThrough &&
      !s.excluded &&
      s.weightRule?.mode !== 'static',
  );
  const myScore = Math.max(0, row.rawScore ?? 0);
  const totalScore = free.reduce((sum, s) => sum + Math.max(0, s.rawScore ?? 0), 0);
  const shareFrac = totalScore > 0 ? myScore / totalScore : 1 / Math.max(1, free.length);
  const freeBudget = 100 - rules.pinnedPerMille / 10;
  const floors = free.length * bounds.minWeight;
  const remaining = Math.max(0, freeBudget - floors);
  const steps: CalcStep[] = [
    {
      label: 'Your share of the unpinned pool',
      formula:
        `your ${score(myScore)} ÷ ${score(totalScore)} ` +
        `(sum of the ${free.length} unpinned competitors)`,
      result: `${(shareFrac * 100).toFixed(1)}%`,
    },
    {
      label: 'Optimal weight',
      formula:
        `pinned rules reserve ${pct(rules.pinnedPerMille)} — ` +
        (bounds.minWeight > 0 ? `${bounds.minWeight}% floor + ` : '') +
        `your ${(shareFrac * 100).toFixed(1)}% of the ${remaining.toFixed(0)}% left`,
      result: pct(row.optimalPerMille),
    },
  ];
  if (rules.boostAppliedPerMille > 0) {
    steps.push({
      label: 'Boost funding',
      formula:
        `boosted rows then draw +${pct(rules.boostAppliedPerMille)} in total ` +
        'from the unboosted rows (already reflected above)',
      result: '',
    });
  }
  return steps;
}
